import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { ChevronRight, ChevronDown, Building2, Users, UserCheck, User, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const formatNumber = (num, prefix = '') => {
  if (num === null || num === undefined) return prefix + '0';
  const absNum = Math.abs(num);
  if (absNum >= 1000000) return prefix + (num / 1000000).toFixed(2) + 'M';
  if (absNum >= 1000) return prefix + (num / 1000).toFixed(1) + 'k';
  return prefix + num.toLocaleString();
};

const OrgSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="flex justify-between items-center mb-4">
      <div className="h-6 w-48 bg-slate-200 rounded" />
      <div className="h-8 w-24 bg-slate-100 rounded" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[1,2,3,4].map((i) => <div key={i} className="bg-slate-100 rounded-lg h-16" />)}
    </div>
    <div className="space-y-2">
      {[1,2,3,4,5].map((i) => <div key={i} className="bg-slate-100 rounded-lg h-14" />)}
    </div>
  </div>
);

// More prominent type styling with larger icons and badges
const TypeConfig = {
  location: { 
    Icon: MapPin, 
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    iconBg: 'bg-blue-500',
    badge: 'Location',
    badgeColor: 'bg-blue-500 text-white'
  },
  department: { 
    Icon: Building2, 
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    iconBg: 'bg-purple-500',
    badge: 'Department',
    badgeColor: 'bg-purple-500 text-white'
  },
  manager: { 
    Icon: UserCheck, 
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    iconBg: 'bg-amber-500',
    badge: 'Manager',
    badgeColor: 'bg-amber-500 text-white'
  },
  employee: { 
    Icon: User, 
    color: 'bg-slate-100 text-slate-700 border-slate-300',
    iconBg: 'bg-slate-500',
    badge: 'Employee',
    badgeColor: 'bg-slate-500 text-white'
  }
};

// Flatten the tree for rendering - now tracks parent context for drill-down
const flattenTree = (nodes, level = 0, parentKey = '', parentLocation = null, parentDepartment = null) => {
  const result = [];
  if (!nodes) return result;
  nodes.forEach((node, idx) => {
    const nodeKey = parentKey ? `${parentKey}/${node.name}` : node.name;
    
    // Determine the location/department context based on node type
    let currentLocation = parentLocation;
    let currentDepartment = parentDepartment;
    
    if (node.type === 'location') {
      currentLocation = node.name;
      currentDepartment = null;
    } else if (node.type === 'department') {
      currentDepartment = node.name;
    }
    
    result.push({ 
      ...node, 
      level, 
      nodeKey,
      parentLocation: currentLocation,
      parentDepartment: currentDepartment
    });
    
    if (node.children) {
      result.push(...flattenTree(node.children, level + 1, nodeKey, currentLocation, currentDepartment));
    }
  });
  return result;
};

const OrgStructure = ({ onDrillDown }) => {
  const { API } = React.useContext(AppContext);
  const { filters } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});

  // Create stable filter key for dependency tracking
  const filterKey = JSON.stringify({
    start_date: filters?.start_date || null,
    end_date: filters?.end_date || null,
    location: filters?.location || null,
    department: filters?.department || null,
    manager: filters?.manager || null,
    pay_types: filters?.pay_types || []
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/org-structure`, {
        start_date: filters?.start_date || undefined,
        end_date: filters?.end_date || undefined,
        location: filters?.location || undefined,
        department: filters?.department || undefined,
        manager: filters?.manager || undefined,
        pay_types: filters?.pay_types?.length > 0 ? filters.pay_types : undefined
      });
      setData(response.data);
      // Expand first level by default
      const initialExpanded = {};
      response.data.org_tree?.forEach(loc => { initialExpanded[loc.name] = true; });
      setExpandedNodes(initialExpanded);
    } catch (error) {
      console.error('Error fetching org structure:', error);
      toast.error('Failed to load organization structure');
    } finally {
      setLoading(false);
    }
  }, [API, filterKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleNode = (key, e) => {
    e.stopPropagation();
    setExpandedNodes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle node click with hierarchical context
  const handleNodeClick = (node) => {
    if (node.type === 'employee') return; // Don't drill down on employees
    
    if (node.type === 'location') {
      // Location click: filter by location only
      onDrillDown?.({ type: 'location', value: node.name });
    } else if (node.type === 'department') {
      // Department click: filter by location AND department
      onDrillDown?.({ 
        type: 'department', 
        value: node.name,
        location: node.parentLocation // Pass location context
      });
    } else if (node.type === 'manager') {
      // Manager click: filter by location, department, AND manager
      onDrillDown?.({ 
        type: 'manager', 
        value: node.name,
        location: node.parentLocation,
        department: node.parentDepartment
      });
    }
  };

  // Check if a node should be visible based on parent expansion state
  const isNodeVisible = (nodeKey) => {
    const parts = nodeKey.split('/');
    if (parts.length === 1) return true;
    let currentKey = '';
    for (let i = 0; i < parts.length - 1; i++) {
      currentKey = currentKey ? `${currentKey}/${parts[i]}` : parts[i];
      if (!expandedNodes[currentKey]) return false;
    }
    return true;
  };

  if (loading) return <OrgSkeleton />;

  if (!data?.org_tree?.length) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="org-structure-empty">
        <Building2 className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p>No organization data available for current filters</p>
      </div>
    );
  }

  const flatNodes = flattenTree(data.org_tree);

  return (
    <div className="space-y-4" data-testid="org-structure">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
            <Building2 className="h-5 w-5" />Organization Structure
          </h3>
          <p className="text-sm text-muted-foreground">Click on any row to view employees. Hierarchical view of locations, departments, and managers.</p>
        </div>
      </div>

      {data.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-md bg-blue-500"><MapPin className="h-3 w-3 text-white" /></div>
              <span className="text-xs text-muted-foreground">Locations</span>
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">{data.summary.total_locations}</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-md bg-purple-500"><Building2 className="h-3 w-3 text-white" /></div>
              <span className="text-xs text-muted-foreground">Departments</span>
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">{data.summary.total_departments}</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-md bg-amber-500"><UserCheck className="h-3 w-3 text-white" /></div>
              <span className="text-xs text-muted-foreground">Managers</span>
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">{data.summary.total_managers}</div>
          </div>
          <div className="bg-white rounded-lg border border-border p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-md bg-slate-500"><Users className="h-3 w-3 text-white" /></div>
              <span className="text-xs text-muted-foreground">Employees</span>
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">{data.summary.total_employees}</div>
          </div>
        </div>
      )}

      {data.summary?.total_premium_hours > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div><div className="text-xs text-orange-600 font-medium">Total Premium Hours</div><div className="text-2xl font-bold text-orange-700">{formatNumber(data.summary.total_premium_hours)}h</div></div>
            <div className="h-10 w-px bg-orange-200" />
            <div><div className="text-xs text-slate-600 font-medium">Total Hours</div><div className="text-2xl font-bold text-slate-700">{formatNumber(data.summary.total_hours)}h</div></div>
            <div className="h-10 w-px bg-orange-200" />
            <div><div className="text-xs text-slate-600 font-medium">Premium %</div><div className={`text-2xl font-bold ${data.summary.premium_percent > 20 ? 'text-red-600' : data.summary.premium_percent > 15 ? 'text-orange-600' : 'text-green-600'}`}>{data.summary.premium_percent.toFixed(1)}%</div></div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-border p-4">
        {/* Legend with prominent icons */}
        <div className="flex flex-wrap items-center gap-4 mb-4 pb-3 border-b border-border">
          <span className="text-sm text-muted-foreground">Legend:</span>
          <div className="flex items-center gap-1.5">
            <div className="p-1 rounded bg-blue-500"><MapPin className="h-3 w-3 text-white" /></div>
            <span className="text-xs font-medium">Location</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="p-1 rounded bg-purple-500"><Building2 className="h-3 w-3 text-white" /></div>
            <span className="text-xs font-medium">Department</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="p-1 rounded bg-amber-500"><UserCheck className="h-3 w-3 text-white" /></div>
            <span className="text-xs font-medium">Manager</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="p-1 rounded bg-slate-500"><User className="h-3 w-3 text-white" /></div>
            <span className="text-xs font-medium">Employee</span>
          </div>
        </div>
        
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {flatNodes.filter(node => isNodeVisible(node.nodeKey)).map((node, idx) => {
            const config = TypeConfig[node.type] || TypeConfig.department;
            const Icon = config.Icon;
            const hasChildren = node.children && node.children.length > 0;
            const isExpanded = expandedNodes[node.nodeKey];
            const isClickable = node.type !== 'employee';

            return (
              <div
                key={node.nodeKey + idx}
                className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${config.color} ${isClickable ? 'cursor-pointer hover:shadow-md hover:scale-[1.01]' : ''}`}
                style={{ marginLeft: node.level * 24 }}
                onClick={() => isClickable && handleNodeClick(node)}
                data-testid={`org-node-${node.type}`}
              >
                {/* Expand/Collapse Button */}
                <button
                  onClick={(e) => toggleNode(node.nodeKey, e)}
                  className={`p-1 rounded transition-colors ${hasChildren ? 'hover:bg-white/50' : 'opacity-0 pointer-events-none'}`}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {/* Prominent Icon with colored background */}
                <div className={`p-2 rounded-lg ${config.iconBg}`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                
                {/* Node Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{node.name}</span>
                    {/* Type Badge */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${config.badgeColor}`}>
                      {config.badge}
                    </span>
                    {node.job_title && <span className="text-xs text-muted-foreground truncate">({node.job_title})</span>}
                  </div>
                  {node.type !== 'employee' && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {node.employee_count} {node.type === 'manager' ? 'direct reports' : 'employees'}
                      {isClickable && <span className="ml-2 text-blue-600">• Click to view employees</span>}
                    </div>
                  )}
                </div>
                
                {/* Metrics */}
                <div className="flex items-center gap-3 text-xs">
                  {node.premium_hours > 0 && (
                    <div className="flex items-center gap-1 bg-white/80 px-2 py-1 rounded">
                      <Clock className="h-3 w-3 text-orange-500" />
                      <span className="font-medium text-orange-600">{formatNumber(node.premium_hours)}h</span>
                    </div>
                  )}
                  {node.premium_percent > 0 && (
                    <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                      node.premium_percent > 20 ? 'bg-red-500 text-white' :
                      node.premium_percent > 15 ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'
                    }`}>{node.premium_percent.toFixed(1)}%</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrgStructure;
